import { postJson } from "./http";

/**
 * Module-wise maker–checker — the six module tabs. Per tab: who makes, who
 * checks, and what each of the tab's functions does.
 *
 * Same contract as teamApi: every verb, reads and writes alike, answers with
 * the FULL screen state, so the screen never patches its own copy and drifts
 * away from the server's gates. Refusals arrive as readable sentences under
 * `errors.message` ("Add them to the client team first…", "Unknown module tab").
 *
 * Seating never invents a person: the roster (onboarding_team_members, owned by
 * teamApi) is the identity home, so somebody has to be added there before they
 * can hold a seat.
 */

/** What a function does. `blocked` is CHECKER-ONLY — hidden from Makers. */
export type SeatPolicy = "post" | "review" | "blocked";

export type SeatKind = "maker" | "checker";

/**
 * A seated person. `orphan` marks a seat whose roster row is gone — the server
 * renders it rather than hiding it, because go-live would otherwise push a
 * permission for somebody the screen never showed.
 */
export interface SeatPersonDTO {
  user_id: number;
  name: string;
  email: string;
  orphan?: boolean;
}

/** One function on a tab, with its effective policy and the firm's default. */
export interface SeatFunctionDTO {
  key: string;
  label: string;
  module: string;
  module_label: string;
  policy: SeatPolicy;
  default: SeatPolicy;
  /** True when this client has been moved off the firm default. */
  changed: boolean;
}

/** The server's verdict on one tab — what the strip's marker reads. */
export interface SeatGateDTO {
  status: "ok" | "empty" | "block" | "warn";
  message: string | null;
  makers: number;
  checkers: number;
  reviews: number;
}

export interface SeatTabDTO {
  key: string;
  label: string;
  /** The tenant permission modules this tab covers. */
  modules: string[];
  makers: SeatPersonDTO[];
  checkers: SeatPersonDTO[];
  functions: SeatFunctionDTO[];
  counts: Record<SeatPolicy, number>;
  changed_count: number;
  gate: SeatGateDTO;
}

/** A roster member, with the seat they hold on each tab (tab key => seat). */
export interface SeatMemberDTO {
  user_id: number;
  name: string;
  email: string;
  seats: Record<string, SeatKind | undefined>;
}

/** One gate verdict — a Continue block or a warning. */
export interface SeatVerdictDTO {
  scope: "tab" | "client";
  tab: string | null;
  label: string | null;
  message: string;
}

/** The whole screen in one payload — every seats endpoint answers with this. */
export interface SeatsStateDTO {
  tabs: SeatTabDTO[];
  members: SeatMemberDTO[];
  policies: SeatPolicy[];
  seats: SeatKind[];
  validation: {
    ok: boolean;
    blocks: SeatVerdictDTO[];
    warnings: SeatVerdictDTO[];
  };
}

interface SeatsEnvelope {
  success: boolean;
  message: string;
  data: SeatsStateDTO;
}

const seatsCall = async (path: string, body: Record<string, unknown>): Promise<SeatsStateDTO> =>
  (await postJson<SeatsEnvelope>(path, body, ["message"])).data;

export const fetchSeatsState = (publicId: string): Promise<SeatsStateDTO> =>
  seatsCall("/api/onboarding/seats/state", { public_id: publicId });

/**
 * Seat someone on a tab, or move them between its two seats — one seat per
 * person per tab, so maker → checker REPLACES the row rather than adding one.
 * Refuses anyone who is not on the client's roster.
 */
export const setSeat = (
  publicId: string,
  moduleKey: string,
  userId: number,
  seat: SeatKind
): Promise<SeatsStateDTO> =>
  seatsCall("/api/onboarding/seats/set", {
    public_id: publicId,
    module_key: moduleKey,
    user_id: userId,
    seat,
  });

/** Unseat someone from one tab. Their roster row and other tabs are untouched. */
export const removeSeat = (publicId: string, moduleKey: string, userId: number): Promise<SeatsStateDTO> =>
  seatsCall("/api/onboarding/seats/remove", {
    public_id: publicId,
    module_key: moduleKey,
    user_id: userId,
  });

/**
 * Set one function's policy. The store is a differences store: setting a
 * function back to the firm default erases the row, so a later change to the
 * firm's defaults reaches this client.
 */
export const setFunctionPolicy = (
  publicId: string,
  ruleKey: string,
  value: SeatPolicy
): Promise<SeatsStateDTO> =>
  seatsCall("/api/onboarding/seats/policy", { public_id: publicId, rule_key: ruleKey, value });

/** One tab back to the firm defaults — every difference on that tab, gone. */
export const resetTabPolicies = (publicId: string, tab: string): Promise<SeatsStateDTO> =>
  seatsCall("/api/onboarding/seats/reset-tab", { public_id: publicId, tab });
